import type { IncidentDetailsDtoBase } from '@/gen/types/IncidentDetailsDtoBase';
import { defaultIncidentAudience, type IncidentAudience } from '@/incidents/forms/audience';
import type { IncidentRichText } from '@/lib/jira-wiki';
import { resolveIncidentRichText } from '@/lib/incident-rich-text';
import { humanizeIdentifier, identifierToKebab } from '@/lib/string-format';
import { buildIncidentDetailFields, type IncidentDetailField } from '@/views/incident/incident-detail-fields';
import type { TagVariant } from '@/components/tables/types';

/**
 * Shared incident-detail view model + mapping, extracted from Incident-detail.vue so the customer
 * incident view (org router) and the NOC incident view (admin router, ITDEVELOP-2693) render the
 * detail card + meta sidebar from one source of truth and stay visually identical.
 */
export interface IncidentDetailViewModel {
    id: string;
    reference: string;
    title: string;
    description: IncidentRichText;
    type: {
        name: string;
        icon: string;
    };
    status: string;
    priority?: string;
    /**
     * Internal (NOC) priority — present only on NOC/admin detail responses (ITDEVELOP-2798);
     * undefined for customers. When set, the meta sidebar shows Internal + Customer priority side by side.
     */
    internalPriority?: string;
    category?: string;
    reporter: {
        name: string;
    };
    created: string;
    responseTime?: string;
    /**
     * When set, this ticket has been linked under another incident (the value is that incident's key).
     * The detail view freezes the comment composer and shows a notice linking to that incident.
     */
    linkedMasterKey?: string;
    location?: string;
    /**
     * Affected location UUIDs from the structured incident location DTO. Preserved (unlike the
     * flattened `location` display string) so the detail view can render the affected branches of
     * the org location tree. Empty/absent for Project- or scope-only incidents with no pinned locations.
     */
    locationIds?: string[];
    /**
     * Legacy Jira site labels (e.g. "ICE02"). Fallback source for the affected-locations sidebar when
     * `locationIds` is absent — current incidents still carry only `sites` (ITDEVELOP-2917).
     */
    locationSites?: string[];
    detailFields: IncidentDetailField[];
}

interface RemoteAttachmentLike {
    id?: string;
    fileName?: string;
    name?: string;
    contentType?: string | null;
}

const HEALTH_SAFETY_TYPES = new Set(['PeopleIncident', 'WasteManagement', 'FireExplosion', 'ChemicalSpillEmissions']);
const SECURITY_TYPES = new Set(['PhysicalSecurity', 'InformationSecurityCybersecurity', 'ThirdParty']);
const COMPLIANCE_QUALITY_TYPES = new Set([
    'CustomerSatisfaction',
    'LegalRegulatory',
    'PolicyCompliance',
    'SupplierPerformance',
    'ServiceQualityContractualDeviation',
]);
const OBSERVATION_TYPES = new Set(['ActionableObservation', 'PositiveObservation']);

export function getIncidentCategory(type: string): string {
    if (OBSERVATION_TYPES.has(type)) return 'Observations';
    if (HEALTH_SAFETY_TYPES.has(type)) return 'Health, Safety & Environment Incident';
    if (SECURITY_TYPES.has(type)) return 'Security Incident';
    if (COMPLIANCE_QUALITY_TYPES.has(type)) return 'Compliance & Quality Incident';
    return 'Operational / Infrastructure Incident';
}

export function formatIncidentLocation(location: IncidentDetailsDtoBase['location']): string {
    if (!location) return '';

    if (location.scope === 'Location' && location.sites?.length) return location.sites.join(', ');
    if (location.scope === 'Project' && location.projectName) return location.projectName;

    const parts: string[] = [];
    if (location.sites?.length) parts.push(location.sites.join(', '));
    if (location.projectName) parts.push(location.projectName);

    if (parts.length > 0) return parts.join(' / ');
    return humanizeIdentifier(location.scope, { casing: 'title' });
}

export function getPriorityVariant(priority: string): TagVariant {
    switch (priority.replace(/\s+/g, '').toLowerCase()) {
        case 'crisis':
        case 'critical':
            return 'danger';
        case 'high':
            return 'warning';
        case 'medium':
            return 'gold';
        case 'low':
        case 'verylow':
            return 'success';
        case 'underreview':
            return 'info';
        default:
            return 'default';
    }
}

/** Formats a wire priority enum name (e.g. 'UnderReview') for display ('Under review'). */
export function formatIncidentPriority(priority: string): string {
    const spaced = priority.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function getResponseTimeVariant(responseTime: string): TagVariant {
    switch (responseTime) {
        case '15m':
            return 'success';
        case '30m':
            return 'info';
        case '1h':
            return 'warning';
        default:
            return 'default';
    }
}

export function mapIncidentDetailViewModel(
    details: IncidentDetailsDtoBase,
    audience: IncidentAudience = defaultIncidentAudience,
): IncidentDetailViewModel {
    const detailAttachments = (details as { attachments?: RemoteAttachmentLike[] }).attachments ?? [];
    const description = resolveIncidentRichText(details.description, {
        attachments: detailAttachments.map((attachment) => ({
            id: String(attachment.id ?? ''),
            fileName: attachment.fileName ?? attachment.name ?? '',
            contentType: attachment.contentType,
        })),
    });
    const extensions = details as IncidentDetailsDtoBase & {
        priority?: string | null;
        internalPriority?: string | null;
        responseTime?: string | null;
        linkedMasterKey?: string | null;
    };

    return {
        id: details.incidentId,
        reference: details.incidentId,
        title: details.summary,
        description,
        type: {
            name: humanizeIdentifier(details.type, { casing: 'title' }),
            icon: identifierToKebab(details.type),
        },
        status: humanizeIdentifier(details.status, { casing: 'title' }),
        category: getIncidentCategory(details.type),
        reporter: {
            name: details.reporterName ?? '',
        },
        created: details.createdAt,
        location: formatIncidentLocation(details.location),
        locationIds: details.location?.locationIds ?? undefined,
        locationSites: details.location?.sites ?? undefined,
        detailFields: buildIncidentDetailFields(details, audience),
        priority: extensions.priority ? formatIncidentPriority(extensions.priority) : undefined,
        internalPriority: extensions.internalPriority ? formatIncidentPriority(extensions.internalPriority) : undefined,
        responseTime: extensions.responseTime ?? undefined,
        linkedMasterKey: extensions.linkedMasterKey ?? undefined,
    };
}
